import React from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	SvgIcon,
	Fade,
	Zoom,
	Chip,
	Divider
} from "@mui/material";

// Premium Custom SVG Icons
const ErrorIcon = (props: any) => (
	<SvgIcon {...props} viewBox="0 0 24 24">
		<circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
		<path
			fill="currentColor"
			d="M12 7c.55 0 1 .45 1 1v4c0 .55-.45 1-1 1s-1-.45-1-1V8c0-.55.45-1 1-1zm0 8c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z"
		/>
		<path
			fill="currentColor"
			d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
		/>
	</SvgIcon>
);

const CheckIcon = (props: any) => (
	<SvgIcon {...props} viewBox="0 0 24 24">
		<circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
		<path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
	</SvgIcon>
);

const WarningIcon = (props: any) => (
	<SvgIcon {...props} viewBox="0 0 24 24">
		<path fill="currentColor" d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" opacity="0.2" />
		<path
			fill="currentColor"
			d="M12 2L1 21h22L12 2zm0 3.83L19.17 19H4.83L12 5.83zM11 16h2v2h-2v-2zm0-6h2v4h-2v-4z"
		/>
	</SvgIcon>
);

const InfoIcon = (props: any) => (
	<SvgIcon {...props} viewBox="0 0 24 24">
		<circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
		<path
			fill="currentColor"
			d="M11 7h2v2h-2V7zm0 4h2v6h-2v-6zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
		/>
	</SvgIcon>
);

const FieldIcon = (props: any) => (
	<SvgIcon {...props} viewBox="0 0 24 24">
		<circle cx="12" cy="12" r="3" fill="currentColor" />
	</SvgIcon>
);

// Type definitions
type Severity = "error" | "warning" | "info" | "success";

interface ValidationAlertModalProps {
	open: boolean;
	onClose: () => void;
	title?: string;
	message?: string;
	missingFields?: string[];
	severity?: Severity;
	onConfirm?: () => void;
	confirmText?: string;
	cancelText?: string;
	showConfirm?: boolean;
}

interface ColorScheme {
	main: string;
	light: string;
	dark: string;
	gradient: string;
	icon: React.ReactNode;
}

/**
 * Premium ValidationAlertModal - CRM-style validation modal
 */
const ValidationAlertModal: React.FC<ValidationAlertModalProps> = ({
	open,
	onClose,
	title = "Validation Error",
	message = "Please fill in the following required fields:",
	missingFields = [],
	severity = "error",
	onConfirm,
	confirmText = "OK",
	cancelText = "Close",
	showConfirm = false
}) => {
	// Premium color schemes with gradients
	const colorSchemes: Record<Severity, ColorScheme> = {
		error: {
			main: "#dc2626",
			light: "#fef2f2",
			dark: "#991b1b",
			gradient: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
			icon: <ErrorIcon sx={{ color: "#dc2626", fontSize: 56 }} />
		},
		warning: {
			main: "#d97706",
			light: "#fffbeb",
			dark: "#92400e",
			gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
			icon: <WarningIcon sx={{ color: "#d97706", fontSize: 56 }} />
		},
		info: {
			main: "#0284c7",
			light: "#f0f9ff",
			dark: "#075985",
			gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
			icon: <InfoIcon sx={{ color: "#0284c7", fontSize: 56 }} />
		},
		success: {
			main: "#059669",
			light: "#ecfdf5",
			dark: "#065f46",
			gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
			icon: <CheckIcon sx={{ color: "#059669", fontSize: 56 }} />
		}
	};

	const currentScheme = colorSchemes[severity] || colorSchemes.error;

	// Glass effect style for button
	const glassButtonStyle = {
		textTransform: "none",
		borderRadius: 2,
		py: 1.2,
		px: 4,
		fontWeight: 600,
		fontSize: "0.95rem",
		backgroundColor: "rgba(0, 0, 0, 0.85)",
		color: "#fff",
		backdropFilter: "blur(10px)",
		boxShadow: "0 4px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1)",
		border: "1px solid rgba(255,255,255,0.1)",
		"&:hover": {
			backgroundColor: "rgba(0, 0, 0, 0.95)",
			boxShadow: "0 6px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.15)",
			transform: "translateY(-1px)"
		},
		"&:active": {
			backgroundColor: "rgba(0, 0, 0, 1)",
			transform: "translateY(0)"
		},
		transition: "all 0.2s ease"
	};

	// Alternative glass effect with white/transparent for outlined variant
	const glassOutlinedStyle = {
		textTransform: "none",
		borderRadius: 2,
		py: 1.2,
		px: 4,
		fontWeight: 600,
		fontSize: "0.95rem",
		backgroundColor: "rgba(255, 255, 255, 0.1)",
		color: "#1a1a1a",
		backdropFilter: "blur(10px)",
		boxShadow: "0 2px 12px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
		border: "1px solid rgba(0,0,0,0.15)",
		"&:hover": {
			backgroundColor: "rgba(0, 0, 0, 0.05)",
			boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
			border: "1px solid rgba(0,0,0,0.25)"
		},
		transition: "all 0.2s ease"
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			TransitionComponent={Zoom}
			transitionDuration={300}
			PaperProps={{
				sx: {
					borderRadius: 3,
					overflow: "hidden",
					minWidth: { xs: "92%", sm: 420, md: 480 },
					maxWidth: 520,
					boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)",
					m: 2
				}
			}}
			aria-labelledby="validation-alert-dialog-title"
		>
			{/* Premium Header with Gradient Background */}
			<Box
				sx={{
					background: currentScheme.gradient,
					p: 3,
					position: "relative",
					overflow: "hidden"
				}}
			>
				{/* Decorative circles */}
				<Box
					sx={{
						position: "absolute",
						top: -20,
						right: -20,
						width: 100,
						height: 100,
						borderRadius: "50%",
						background: "rgba(255,255,255,0.1)",
						backdropFilter: "blur(10px)"
					}}
				/>
				<Box
					sx={{
						position: "absolute",
						bottom: -30,
						left: -30,
						width: 140,
						height: 140,
						borderRadius: "50%",
						background: "rgba(255,255,255,0.08)",
						backdropFilter: "blur(10px)"
					}}
				/>

				{/* Icon Container */}
				<Fade in={open} timeout={500}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							position: "relative",
							zIndex: 1
						}}
					>
						<Box
							sx={{
								backgroundColor: "rgba(255,255,255,0.95)",
								borderRadius: "50%",
								p: 1.5,
								boxShadow: "0 10px 25px -5px rgba(0,0,0,0.2)",
								transform: "scale(1)",
								transition: "transform 0.3s ease",
								"&:hover": {
									transform: "scale(1.05)"
								}
							}}
						>
							{currentScheme.icon}
						</Box>
					</Box>
				</Fade>
			</Box>

			{/* Content Section */}
			<DialogContent sx={{ p: 0 }}>
				<Box sx={{ p: 3, pt: 2.5 }}>
					{/* Title with chip badge */}
					<Box sx={{ textAlign: "center", mb: 2 }}>
						<Chip
							label={severity.toUpperCase()}
							size="small"
							sx={{
								backgroundColor: currentScheme.light,
								color: currentScheme.dark,
								fontWeight: 700,
								fontSize: "0.65rem",
								letterSpacing: 0.5,
								mb: 1.5,
								border: `1px solid ${currentScheme.main}20`
							}}
						/>
						<Typography
							variant="h5"
							sx={{
								fontWeight: 700,
								color: "grey.900",
								fontSize: "1.35rem",
								lineHeight: 1.3
							}}
						>
							{title}
						</Typography>
					</Box>

					{/* Message */}
					<Typography
						variant="body1"
						sx={{
							color: "grey.600",
							textAlign: "center",
							mb: missingFields.length > 0 ? 2.5 : 0,
							fontSize: "0.95rem",
							lineHeight: 1.5,
							px: 1
						}}
					>
						{message}
					</Typography>

					{/* Missing Fields List */}
					{missingFields.length > 0 && (
						<>
							<Divider sx={{ my: 2, borderColor: "grey.200" }} />
							<Box
								sx={{
									backgroundColor: "grey.50",
									borderRadius: 2,
									p: 2,
									border: "1px solid",
									borderColor: "grey.200"
								}}
							>
								<Typography
									variant="caption"
									sx={{
										color: "grey.500",
										fontWeight: 600,
										textTransform: "uppercase",
										letterSpacing: 0.5,
										mb: 1.5,
										display: "block"
									}}
								>
									Required Fields ({missingFields.length})
								</Typography>
								<List dense sx={{ p: 0 }}>
									{missingFields.map((field: string, index: number) => (
										<ListItem
											key={index}
											sx={{
												py: 0.75,
												px: 1,
												borderRadius: 1,
												mb: 0.5,
												backgroundColor: "background.paper",
												border: "1px solid",
												borderColor: "grey.200",
												"&:last-child": { mb: 0 },
												transition: "all 0.2s ease",
												"&:hover": {
													borderColor: currentScheme.main,
													boxShadow: `0 2px 8px ${currentScheme.main}15`
												}
											}}
										>
											<ListItemIcon sx={{ minWidth: 28 }}>
												<FieldIcon
													sx={{
														color: currentScheme.main,
														fontSize: 8,
														opacity: 0.8
													}}
												/>
											</ListItemIcon>
											<ListItemText
												primary={
													<Typography
														variant="body2"
														sx={{
															fontWeight: 500,
															color: "grey.800",
															fontSize: "0.9rem"
														}}
													>
														{field}
													</Typography>
												}
											/>
										</ListItem>
									))}
								</List>
							</Box>
						</>
					)}
				</Box>
			</DialogContent>

			{/* Actions - Black/Glass Effect Buttons */}
			<DialogActions
				sx={{
					px: 3,
					pb: 3,
					pt: 0,
					justifyContent: "center",
					gap: 1.5
				}}
			>
				<Button onClick={onClose} sx={glassButtonStyle}>
					{cancelText}
				</Button>

				{showConfirm && onConfirm && (
					<Button onClick={onConfirm} sx={glassOutlinedStyle} autoFocus>
						{confirmText}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default ValidationAlertModal;
